import { apiClient } from "./client";
import type { Paginated, PageQuery } from "@/types/common";
import type {
  CashReconciliationRow,
  CashReconciliationStatus,
  DebtorRow,
  Expense,
  ExpenseCreateInput,
  Payment,
  PaymentCreateInput,
  PnlReport,
  TeacherSalarySummary,
  WriteOffCreateInput,
  WriteOffRow,
} from "@/types/finance";
import type { Charge } from "@/types/finance";

export interface PaymentsQuery extends PageQuery {
  branchId?: string;
  method?: string;
  from?: string;
  to?: string;
  studentId?: string;
}

export async function fetchPayments(query: PaymentsQuery): Promise<Paginated<Payment>> {
  const { data } = await apiClient.get<Paginated<Payment>>("/payments", { params: query });
  return data;
}

export async function createPayment(payload: PaymentCreateInput): Promise<Payment> {
  const { data } = await apiClient.post<Payment>("/payments", payload);
  return data;
}

export async function searchOpenCharges(studentId: string): Promise<Charge[]> {
  const { data } = await apiClient.get<Charge[]>(`/students/${studentId}/charges`, { params: { open: true } });
  return data;
}

export async function fetchDebtors(query: { branchId?: string } & PageQuery = {}): Promise<Paginated<DebtorRow>> {
  const { data } = await apiClient.get<Paginated<DebtorRow>>("/finance/debtors", { params: query });
  return data;
}

export async function sendDebtReminder(studentId: string): Promise<void> {
  await apiClient.post(`/finance/debtors/${studentId}/remind`);
}

export async function sendDebtRemindersToAll(branchId?: string): Promise<{ queued: number }> {
  const { data } = await apiClient.post<{ queued: number }>("/finance/debtors/remind-all", { branchId });
  return data;
}

export interface ExpensesQuery extends PageQuery {
  branchId?: string;
  kind?: string;
  category?: string;
  from?: string;
  to?: string;
}

export async function fetchExpenses(query: ExpensesQuery): Promise<Paginated<Expense>> {
  const { data } = await apiClient.get<Paginated<Expense>>("/expenses", { params: query });
  return data;
}

export async function createExpense(payload: ExpenseCreateInput): Promise<Expense> {
  const { data } = await apiClient.post<Expense>("/expenses", payload);
  return data;
}

export async function fetchTeacherSalary(teacherId: string, period: string): Promise<TeacherSalarySummary> {
  const { data } = await apiClient.get<TeacherSalarySummary>(`/finance/salary/${teacherId}`, { params: { period } });
  return data;
}

export async function fetchSalarySummaries(period: string, branchId?: string): Promise<TeacherSalarySummary[]> {
  const { data } = await apiClient.get<TeacherSalarySummary[]>("/finance/salary", { params: { period, branchId } });
  return data;
}

export async function paySalary(teacherId: string, payload: { period: string; amount: number; comment?: string }): Promise<void> {
  await apiClient.post(`/finance/salary/${teacherId}/pay`, payload);
}

export async function fetchFinanceReport(params: { from?: string; to?: string } = {}): Promise<PnlReport> {
  const { data } = await apiClient.get<PnlReport>("/finance/report", { params });
  return data;
}

export async function createWriteOff(payload: WriteOffCreateInput): Promise<WriteOffRow> {
  const { data } = await apiClient.post<WriteOffRow>("/billing/write-offs", payload);
  return data;
}

export async function fetchWriteOffs(query: { branchId?: string; studentId?: string } = {}): Promise<WriteOffRow[]> {
  const { data } = await apiClient.get<WriteOffRow[]>("/billing/write-offs", { params: query });
  return data;
}

export interface CashReconciliationQuery {
  branchId?: string;
  period?: string;
  status?: CashReconciliationStatus;
}

export async function fetchCashReconciliations(query: CashReconciliationQuery = {}): Promise<CashReconciliationRow[]> {
  const { data } = await apiClient.get<CashReconciliationRow[]>("/billing/cash-reconciliation", { params: query });
  return data;
}

export async function resolveCashReconciliation(id: string, reason?: string): Promise<CashReconciliationRow> {
  const { data } = await apiClient.patch<CashReconciliationRow>(`/billing/cash-reconciliation/${id}/resolve`, { reason });
  return data;
}

export async function downloadFinanceXlsx(params: { branchId?: string; from?: string; to?: string }): Promise<void> {
  const response = await apiClient.get("/finance/export/xlsx", { params, responseType: "blob" });
  const url = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "proschool-finance.xlsx";
  anchor.click();
  URL.revokeObjectURL(url);
}
